/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';

const messages = defineMessages({
    enabled: { id: 'landingPage.resourcesTable.enabled', defaultMessage: 'Enabled' },
    notEnabled: { id: 'landingPage.resourcesTable.notEnabled', defaultMessage: 'Not enabled' }
});

export const ManagedIdentityCell = observer((props: { account: AzureCsAccountStore }) => {
    const { account } = props;
    const tracker = useProgressTracker();
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();

    const { userRole, displayedUserRole } = rootStore.serviceKey;

    const { canChangeAppSettingsAndDetails } = mapUserRoleToUserActions(userRole);

    return (
        <Checkbox
            disabled={tracker.isRunning || !canChangeAppSettingsAndDetails}
            checked={account.isIdentityEnabled}
            onChange={(_, checked) =>
                dispatcher.dispatch('updateAzureCsAccountByPath', {
                    tracker,
                    toastData: {},
                    localStore: account,
                    skuCode: account.sku.code,
                    isIdentityEnabled: checked
                })
            }
            onRenderLabel={() => (
                <RBACTooltip
                    hidden={canChangeAppSettingsAndDetails}
                    displayedUserRole={displayedUserRole}
                    componentSurroundedByToolTip={
                        <Stack horizontal tokens={{ childrenGap: 5 }}>
                            {tracker.isRunning && <Spinner size={SpinnerSize.small} />}
                            <LuisText styles={!canChangeAppSettingsAndDetails && { root: { cursor: 'not-allowed' } }}>
                                {localize(account.isIdentityEnabled ? messages.enabled : messages.notEnabled)}
                            </LuisText>
                        </Stack>
                    }
                />
            )}
        />
    );
});
